import { SEAM, atLines, need, onBeat, saidLines, sayOn } from "../timing.js";
import type { Hit, Landings, PieceInput, Seam } from "../types.js";

/**
 * Steps: a process of two or three steps, one at a time and full frame, standing on a track across the foot of the
 * frame. The first is slammed down a letter at a time on the cut, in reading order, while the bar dives from the middle
 * (behind the words, under them) on to the start of the track; its head then runs to the first node. Each next step
 * comes in on a whip pan (the step before leaves sideways, smeared by its speed, and the next arrives the same way),
 * and as it lands the bar's head snaps on to the next node. The last step, the story's end, holds longest, then is
 * crushed into the bar a row at a time from the bottom while the bar lifts off the track and rises into the cut.
 *
 * Every contact is a whole frame, the same one the picture and the sound use, counted on the music's grid (`onBeat`):
 * a kick before a beat before a half beat, and never at the cost of a step's reading time (`need`). With a voice, each
 * step lands with its word instead, on the half beat at or just before it (`sayOn`), and the voice paces the reading.
 */

/**
 * A whip pan, in frames from its contact: the step before and the next are on one strip of film, pulled across to the
 * left faster and faster; the next step's last letter reaches its place a frame before the contact, carries on a
 * little past it, and the contact is the first frame it stands sharp, the frame the bar's head reaches its node.
 */
export const WHIP = {
  /** The pan starts this many frames before the contact: the step before starts to leave. */
  lead: 6,
  /** Frames from a line's first letter to its last, which trails the first a little. */
  lag: 0.4,
  /** Frames after the contact the arriving step reads as still. */
  settle: 2,
  /** Frames before the contact the whoosh peaks: both steps at full speed, just before the arrival stops dead. */
  peak: 2,
};

/**
 * The last step going into the bar: each row is crushed flat on to it in `dur` frames, the bottom row first and the
 * top row `spread` frames later (about a frame and a half a row), and the last is gone `gone` frames before the cut, so
 * words are still pouring into the bar as it rises.
 */
export const SINK = { dur: 4, spread: 3, gone: 2 };

/** Frames after its last letter lands that the first step reads as still. */
const SLAM_SETTLE = 1;

/**
 * The frames the first step's last letter can land on: no sooner than the bar can dive from the middle on to the track
 * and the slam be seen, and no later than the slam is still the cut's own, about a third of a second after it.
 */
const SLAM = { soonest: 7, latest: 12 };

/**
 * The closest two whips' contacts can be: the step before has landed and settled (`WHIP.settle`), stands still a
 * frame, and the next whip takes it away (`WHIP.lead`). The first whip's contact can come a frame closer to the first
 * step's landing, which settles sooner (`SLAM_SETTLE`).
 */
const APART = WHIP.lead + WHIP.settle + 1;
const FIRST_APART = SLAM_SETTLE + WHIP.lead + 1;

export type StepTime = {
  text: string;
  /** The step has landed: the first step's last letter down, the others' last letter in place and the bar's head on its node. */
  lands: number;
  /** The first frame any of it is on screen (for the first step, its slam's own schedule is the component's). */
  start: number;
  /** The whip that brings it in starts (its contact is `lands`), or null for the first step. */
  whip: number | null;
  /** The frame its letters start to leave on the next step's whip, or null for the last step. */
  leaves: number | null;
  /** From when to when it stands still and can be read. */
  read: [number, number];
  /** Frames it wants to be read. */
  need: number;
};

/** The music's half-beat grid over a piece, as the frames it rounds to, each with how strongly it sits: 3 a kick (every second beat, the cuts are on kicks), 2 a beat, 1 a half beat. */
function gridOf(seam: Seam, frames: number) {
  const out = new Map<number, number>();
  if (!seam.beat || seam.beat <= 6) return out;
  for (let j = 0; ; j++) {
    const at = onBeat(j / 2, seam);
    if (at >= frames) break;
    if (at >= 0 && !out.has(at)) out.set(at, j % 4 === 0 ? 3 : j % 2 === 0 ? 2 : 1);
  }
  return out;
}

const memo = new Map<string, ReturnType<typeof compute>>();
export function stepsTiming(input: Pick<PieceInput, "lines">, frames: number, seam: Seam = { first: false, last: false }) {
  const key = JSON.stringify([input.lines, frames, seam]);
  let t = memo.get(key);
  if (!t) { t = compute(input, frames, seam); if (memo.size > 64) memo.clear(); memo.set(key, t); }
  return t;
}

function compute(input: Pick<PieceInput, "lines">, frames: number, seam: Seam) {
  const lines = input.lines.map((l) => l.trim()).filter(Boolean).slice(0, 3);
  const n = lines.length;
  const grid = gridOf(seam, frames);
  const beat = grid.size ? seam.beat : undefined;
  const strength = (fr: number) => grid.get(fr) ?? 0;
  const back = frames - SEAM.back;
  // The last step's top row is gone into the bar two frames before the cut, and its bottom row started `spread` frames
  // before that (the last piece of a video keeps its words, and holds them to the end).
  const gone = seam.last ? frames : frames - SINK.gone;
  const sink = seam.last ? frames : gone - SINK.dur - SINK.spread;
  // The first step's last letter lands on the first half beat after the cut (about a third of a second), all of it
  // slammed down, in reading order, in the time the bar takes to dive on to the track.
  const near = [...grid.keys()].filter((fr) => fr >= SLAM.soonest && fr <= SLAM.latest).sort((a, b) => Math.abs(a - 9.5) - Math.abs(b - 9.5));
  const needs = lines.map((l) => need(l));
  const until = seam.last ? frames - 2 : sink;
  // The last contact a whip can make: the step it brings in has settled and stood still a frame before it goes.
  const hi = Math.floor(until - WHIP.settle - 1);
  // Said by a voice, each step lands with its word instead (`onVoice`), and the voice paces the reading. A piece too
  // short to land its steps on any voice keeps the rules below.
  const voiced = seam.said && n ? onVoice(saidLines(input.lines, seam), near[0] ?? 9, hi, seam) : null;
  const lands0 = voiced ? voiced[0]! : (near[0] ?? 9);
  const settled0 = lands0 + SLAM_SETTLE;

  // The contacts of the whips: each on the strongest beat it can take while every step keeps its reading time, and the
  // last step, where the story ends, held longest of all. A step that cannot have its time takes as much as there is.
  const holdsOf = (cs: number[]) => {
    const reads = [settled0, ...cs.map((c) => c + WHIP.settle)];
    const ends = [...cs.map((c) => c - WHIP.lead), until];
    return reads.map((r, k) => ends[k]! - r);
  };
  let contacts: number[] = [];
  if (voiced) contacts = voiced.slice(1);
  else if (n > 1) {
    const lo = lands0 + FIRST_APART;
    const surplus = Math.max(0, until - settled0 - (n - 1) * (WHIP.lead + WHIP.settle) - needs.reduce((a, b) => a + b, 0));
    const want = needs.map((x, k) => x + (surplus * (k === n - 1 ? 2 : 1)) / (n + 1));
    let best = { cs: [] as number[], score: -Infinity };
    const walk = (k: number, cs: number[]) => {
      if (k === n - 1) {
        const holds = holdsOf(cs);
        const short = holds.reduce((a, h, i) => a + Math.max(0, needs[i]! - 0.5 - h) ** 2, 0);
        const lastLongest = holds.at(-1)! >= Math.max(...holds.slice(0, -1));
        const score = -100 * short + (lastLongest ? 40 : 0) + cs.reduce((a, c) => a + strength(c), 0) * 10 - holds.reduce((a, h, i) => a + Math.abs(h - want[i]!), 0) * 0.3;
        if (score > best.score) best = { cs: [...cs], score };
        return;
      }
      const from = Math.max(lo, Math.ceil((cs.at(-1) ?? -Infinity) + APART));
      for (let c = from; c <= hi - (n - 2 - k) * APART; c++) walk(k + 1, [...cs, c]);
    };
    walk(0, []);
    contacts = best.cs.length === n - 1 ? best.cs : Array.from({ length: n - 1 }, (_, k) => Math.round(settled0 + ((until - settled0) * (k + 1)) / n));
  }
  // The bar's head reaches the first node on the first beat after the letters land, while the first step holds.
  const firstWhip = contacts[0] !== undefined ? contacts[0] - WHIP.lead : until;
  const beatNode = [...grid.entries()].filter(([fr, s]) => s >= 2 && fr >= lands0 + 6 && fr <= firstWhip - 5).map(([fr]) => fr)[0];
  const node0 = beatNode ?? Math.round(Math.min(lands0 + 9, (lands0 + firstWhip) / 2));

  const steps: StepTime[] = lines.map((text, k) => {
    if (k === 0) return { text, lands: lands0, start: 0, whip: null, leaves: null, read: [settled0, 0], need: needs[k]! };
    const c = contacts[k - 1]!;
    return { text, lands: c, start: c - WHIP.lead - WHIP.lag, whip: c - WHIP.lead, leaves: null, read: [c + WHIP.settle, 0], need: needs[k]! };
  });
  steps.forEach((s, k) => {
    s.leaves = steps[k + 1]?.whip ?? null;
    s.read[1] = s.leaves ?? until;
  });
  return { steps, n, back, gone, sink, lands0, node0, beat, strength: Object.fromEntries(grid) as Record<number, number> };
}

/**
 * Each step's landing on the voice (`sayOn`): the first step's, then each whip's contact. The first is slammed down no
 * sooner and no later than a slam on the cut can be (`SLAM`), on the slam's own half beat when it is not said. Each
 * after it lands no sooner than the step before has landed and stood still (`FIRST_APART`, `APART`), as soon as it can
 * when it is not said, and no later than leaves every step after it its whip and the last its settle by `last`. Null
 * when there is no such frame for a step: the piece is too short to land its steps on any voice.
 */
function onVoice(said: Array<number | null>, slam: number, last: number, seam: Seam): number[] | null {
  const n = said.length, out: number[] = [];
  // The frames the steps after step k need after it: the first whip, then a whip apart for each after that.
  const after = (k: number) => (k < n - 1 ? (k ? APART : FIRST_APART) + (n - 2 - k) * APART : 0);
  for (let k = 0; k < n; k++) {
    const from = k ? out[k - 1]! + (k === 1 ? FIRST_APART : APART) : SLAM.soonest;
    const at = sayOn(said[k] ?? (k ? from : slam), seam, { earliest: from, latest: Math.min(k ? Infinity : SLAM.latest, last - after(k)) });
    if (at === null) return null;
    out.push(at);
  }
  return out;
}

/**
 * Where steps lands each step (`Landings`): the first as its last letter is slammed down, each after it on its whip's
 * contact, the first frame it stands sharp and the bar's head is on its node. Its words go as the last step's bottom
 * row starts into the bar (the last piece of a video keeps them to the end).
 */
export function stepsLandings(input: Pick<PieceInput, "lines">, frames: number, seam: Seam = { first: false, last: false }): Landings {
  const t = stepsTiming(input, frames, seam);
  return { lines: atLines(input.lines, t.steps.map((s) => s.lands)), leaves: t.sink };
}

export function stepsBeats(input: PieceInput, frames: number, seam: Seam = { first: false, last: false }): Hit[] {
  const t = stepsTiming(input, frames, seam);
  const hits: Hit[] = seam.first ? [] : [{ frame: 0, kind: "whoosh" }];
  if (t.n) {
    hits.push({ frame: t.lands0, kind: "thud" });
    hits.push({ frame: t.node0, kind: "pop", step: 0 });
  }
  t.steps.forEach((s, k) => {
    if (s.whip === null) return;
    hits.push({ frame: s.lands - WHIP.peak, kind: "whoosh" });
    hits.push({ frame: s.lands, kind: "pop", step: k });
    // A contact on a kick is the heaviest the piece has: the letters stop dead as well as the node popping, and the
    // music gives way under it so the pop is not lost in the kick.
    if ((t.strength[s.lands] ?? 0) === 3) hits.push({ frame: s.lands, kind: "thud" });
  });
  // The last row of the last step going into the bar.
  if (t.n && !seam.last) hits.push({ frame: t.gone, kind: "tick" });
  return hits.filter((h) => h.frame >= 0 && h.frame < frames).sort((a, b) => a.frame - b.frame);
}

